import React, { useRef, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Slider from 'react-slick';
import { PlayIcon, LeftCaretIcon, RightCaretIcon } from './Icons';
import YoutubeEmbed from '../components/shared/YoutubeEmbed';
import Preview3d from '../components/shared/Preview3D';
import { useSwipeable } from 'react-swipeable';

const CustomCarouselWithThumbs = (props) => {
  const { carouselOptions, content } = props;
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const slider = useRef(null);
  const [turntableImagesArray, setTurntableImagesArray] = useState(null);
  const [showVideoPlay, setShowVideoPlay] = useState<boolean>(true);
  const [startIndex, setStartIndex] = useState<number>(0);
  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [showControls, setShowControls] = useState({});
  const [isVideoPlaying, setIsVideoPlaying] = useState({});
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [thumbnailsPerSet, setThumbnailsPerSet] = useState<number>(6);
  const router = useRouter();
  const [ is3DPage, setIs3DPage ] = useState(router.asPath.includes('/3d/'));
  const videoRefs = useRef({});

  const calculateThumbnailsPerSet = () => {
    const breakpoints = [
      { width: 320, thumbnails: 3, isMobile: true },
      { width: 375, thumbnails: 4, isMobile: true },
      { width: 758, thumbnails: 5, isMobile: true },
      { width: 768, thumbnails: defineThumbnailsPerSet(3), isMobile: true },
      { width: 1024, thumbnails: defineThumbnailsPerSet(2), isMobile: false, exactMatch: true },
      { width: 1440, thumbnails: defineThumbnailsPerSet(3), isMobile: false },
      { width: 2560, thumbnails: defineThumbnailsPerSet(5), isMobile: false },
    ];

    const defaultSetting = { isMobile: false, thumbnails: is3DPage ? 7 : 5};

    const windowWidth = window.innerWidth;
    let setting = defaultSetting;

    for (const breakpoint of breakpoints) {
      const { width, thumbnails, isMobile, exactMatch } = breakpoint;
      if ((exactMatch && windowWidth === width) || (!exactMatch && windowWidth <= width)) {
        setting = { isMobile, thumbnails };
        break;
      }
    }

    return setting;
  };

  function defineThumbnailsPerSet(n) {
    if (is3DPage && isMobile) return n + 1;
    if (is3DPage && !isMobile) return n + 2;
    return n;
  }

  useEffect(() => {
    const handleResize = () => {
      const { isMobile: newIsMobile, thumbnails: newThumbnailsPerSet } = calculateThumbnailsPerSet();
      setIsMobile(newIsMobile);
      setThumbnailsPerSet(newThumbnailsPerSet);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const playVideo = (k) => {
    const selectedVideoRef = videoRefs.current[k];
    if (selectedVideoRef) {
      if (selectedVideoRef.paused || selectedVideoRef.ended) {
        selectedVideoRef.play();
        setShowVideoPlay(false);
        setIsVideoPlaying((prevPlaying) => ({ ...prevPlaying, [k]: true }));
      } else {
        selectedVideoRef.pause();
      }
    }
};

  const toggleVideoPlay = (k) => (setIsVideoPlaying((prevPlaying) => ({ ...prevPlaying, [k]: !prevPlaying[k] })), playVideo(k));

  const thumbnailStyleBasedOn3D = is3DPage && 'rounded-[5px]'

  const turntableImages = (media) => {
    const rangePattern = /\[(\d+)-(\d+)\]\.jpg/;
    const match = media.video_url.match(rangePattern);

    if (!match) {
      return [];
    }
    const start = parseInt(match[1], 10);
    const end = parseInt(match[2], 10);
    const baseUrl = media.video_url.replace(rangePattern, '');
    let urls = [];
    for (let i = start; i <= end; i++) {
      let formattedNumber = i.toString().padStart(2, '0');
      let url = `${baseUrl}${formattedNumber}.jpg`;
      urls.push(url);
    }
    return urls;
  };

  useEffect(() => {
    if (turntableImagesArray) return;
    const turntableMedia = content?.filter((product_media: any) => product_media.role === 'Turntable');

    if (!turntableMedia?.length) return;
    const imagesArray = turntableImages(turntableMedia[0]).reverse();

    setTurntableImagesArray(imagesArray);
  }, [content]);

  const handleSlideChange = (index) => {
    setActiveIndex(index);
  };

  const handleThumbnailClick = (index) => {
    setActiveIndex(index);
    if (slider.current) {
      slider.current.slickGoTo(index);
    }
  };

  const handleTouchEnd = (e, index) => {
    e.preventDefault();
    handleThumbnailClick(index);
  };

  const handleArrowClick = (type) => {
    if (isNavigating) return;
    setIsNavigating(true);

    let nextIndex = activeIndex;
    if (type === 'next') {
      nextIndex = (activeIndex + 1) % content.length;
    } else if (type === 'prev') {
      nextIndex = (activeIndex - 1 + content.length) % content.length;
    }

    if (nextIndex < startIndex) {
      setStartIndex(nextIndex);
    } else if (nextIndex >= startIndex + thumbnailsPerSet) {
      setStartIndex(nextIndex - thumbnailsPerSet + 1);
    }

    setActiveIndex(nextIndex);
    setTimeout(() => setIsNavigating(false), 300);
  };

  useEffect(() => {
    if (slider.current) {
      slider.current.slickGoTo(activeIndex);
    }
  }, [activeIndex]);

  const handleThumbnailSwipeLeft = () => {
      if (content.length <= thumbnailsPerSet) return;
      const nextStartIndex = Math.min(startIndex + thumbnailsPerSet, content.length - thumbnailsPerSet);
      setStartIndex(nextStartIndex);
  };

  const handleThumbnailSwipeRight = () => {
      if (content.length <= thumbnailsPerSet) return;
      const prevStartIndex = Math.max(0, startIndex - thumbnailsPerSet);
      setStartIndex(prevStartIndex);
  };

  const thumbnailHandlers = useSwipeable({
    onSwipedLeft: handleThumbnailSwipeLeft,
    onSwipedRight: handleThumbnailSwipeRight,
  });

  const isYoutube = (v) => { return v.subtype == 'youtube' || v.type == 'youtube' || v.youtube_link?.length > 0; };

  const renderPreview3D = (contentItem: any) => {
    const _turntableImages = turntableImages(contentItem);
    return <Preview3d animationFrameUrls={_turntableImages} posterImage={_turntableImages[0]} />;
  };

  const thumbnailClass = (k) => {
    const globalIndex = k + startIndex;

    return globalIndex === activeIndex
      ? 'border-[.2px] border-[#7F898D]'
      : 'brightness-[70%] hover:brightness-[100%] hover:border hover:border-[#7F898D] hover:border-[.2px]';
  };

  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const handleArrowClickDebounced = debounce(handleArrowClick, 300);

  // FEATURED CONTENT
  const activeContent = content?.map((v, k) => (
    <div key={k}>
      {v.type == 'Image' || v.type == 'image' && <img src={v.url} className={`overflow-hidden ${thumbnailStyleBasedOn3D}`} />}

      {v.role == 'Image' && <img src={v.url} className={`overflow-hidden ${thumbnailStyleBasedOn3D}`} />}

      {v.role == 'Turntable' && !!turntableImages(v) && renderPreview3D(v)}

      {isYoutube(v) && <YoutubeEmbed embedId={v.youtube_link || v.url} className="z-40 bg-black" autoplay={false} />}

      {/******** NON-YOUTUBE VIDEOS ********/}
      {v.type == 'video' && v?.subtype == 'featured_video' && (
        <div
          className="z-40 flex cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            toggleVideoPlay(k);
          }}
          style={{ zIndex: showControls[k] ? 40 : 50 }}>
          <video
            className={`"w-screen md:w-full ${showControls[k] ? 'z-30' : 'z-50'}`}
            poster={v.image}
            loop
            controls={showControls[k]}
            muted
            ref={(el) => {
              videoRefs.current[k] = el;
              if (el) {
                el.onplay = () => setIsVideoPlaying((prevPlaying) => ({ ...prevPlaying, [k]: true }));
                el.onpause = () => setIsVideoPlaying((prevPlaying) => ({ ...prevPlaying, [k]: false }));
              }
            }}
            onMouseOver={() => setShowControls((prevControls) => ({ ...prevControls, [k]: true }))}
            onMouseOut={() => setShowControls((prevControls) => ({ ...prevControls, [k]: false }))}
          >
            <source src={v.url} type="video/mp4" />
          </video>
          {showVideoPlay && (
            <div
              className={`video-overlay-btn cursor-pointer ${showControls[k] ? 'z-40' : 'z-50'}`}
              onClick={(e) => {
                e.stopPropagation();
                playVideo(k);
              }}
              id={'videobtn' + k}
            >
              <PlayIcon className="w-[100px]" />
            </div>
          )}
        </div>
      )}

      <div className={`absolute ${router.asPath.includes('/3d') ? 'flex' : 'hidden'} items-center bottom-5 left-5 ${!isVideoPlaying[k] && 'z-50'} w-fit h-fit rounded-[5px] bg-[#1317234D] bg-opacity-[30%] text-[14px] text-[#AEB6BA] px-1 py-1 font-400 leading-[22px]`}>
        {activeIndex + 1}/{content.length}
      </div>
    </div>
  ));


  const renderThumbnail = (media, index) => {
    if (media.type === 'Image' || media.type === 'image' || media.role === 'Image') {
      return <img src={media.url} alt="Image Thumbnail" className='thumbnail-image'/>
    } else if (media.type === 'youtube' && media.url.length === 11 || media.role === 'Video' || media?.subtype === 'youtube' || media?.subtype === 'featured_video' && media.video_url != '') {
      return <YoutubeThumbnail media={media} index={index} />
    } else if (media.role === 'Turntable') {
      return <Thumbnail3D media={media} index={index} />
    } else {
      return <img src={media.image} alt="Image Thumbnail" className='thumbnail-image'/>
    }
  };

  const YoutubeThumbnail = ({ media, index }) => {
    const thumbnailUrl = media.image || `https://img.youtube.com/vi/${media.url || media.youtube_link}/default.jpg`;
    return (
      <div className={`relative w-full h-full overflow-hidden flex items-center justify-center bg-[#1C1F20]`}>
        <img src={thumbnailUrl} alt="YouTube Thumbnail" className="thumbnail-image" />
        <PlayIcon className="absolute w-[36px] h-[36px] z-20 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
      </div>
    );
  };

  const Thumbnail3D = ({ media, index }) => {
    return (
       <div className="relative flex items-center justify-center">
        <div className={`absolute top-1.5 left-1.5 w-[32px] bg-blue-1000 rounded-1 text-white text-[12px] rounded-[3px] leading-none z-20 p-1`}>
          360
        </div>
        <img key={index} src={turntableImages(media)[0]} alt="Image Thumbnail" />
      </div>
    )
  };

  const renderThumbnailContent = (): React.ReactElement => {
    const actualThumbnailsToShow = Math.min(thumbnailsPerSet, content.length);
    const endIndex = startIndex + actualThumbnailsToShow;
    const thumbnailsToShow = [];

    for (let i = startIndex; i < endIndex; i++) {
      const wrappedIndex = i % content.length;
      thumbnailsToShow.push(
        <div
          key={wrappedIndex}
          onTouchEnd={(e) => handleTouchEnd(e, wrappedIndex)}
          onClick={() => handleThumbnailClick(wrappedIndex)}
          className={`max-w-[140px] w-full h-full aspect-video ${thumbnailClass(wrappedIndex - startIndex)}`}
        >
          {renderThumbnail(content[wrappedIndex], wrappedIndex)}
        </div>
      );
    }

    return (
      <div className='flex mx-1 2xl:justify-center xl:mx-4 2xl:mx-2 max-h-[80px] w-full gap-1 md:gap-2' {...thumbnailHandlers}>
        {thumbnailsToShow}
      </div>
    );
  };

  const renderLeftArrow = () => {
    if (content.length <= 1) return null;

    return (
        <div
          onClick={() => handleArrowClick('prev')}
          onDoubleClick={(e) => e.preventDefault()}
          className={`hidden lg:flex items-center justify-center bg-white bg-opacity-[5%] min-w-[42px] h-[42px] rounded cursor-pointer hover:bg-opacity-[20%] stroke-[#7F898D] hover:stroke-white`}
        >
          <LeftCaretIcon className="pr-1 w-[15px]" />
        </div>
    );
  };

  const renderRightArrow = () => {
    if (content.length <= 1) return null;

    return (
      <>
        <div
          onClick={() => handleArrowClick('next')}
          onDoubleClick={(e) => e.preventDefault()}
          className={`hidden lg:flex items-center justify-center bg-white bg-opacity-[5%] min-w-[42px] h-[42px] rounded cursor-pointer hover:bg-opacity-[20%] stroke-[#7F898D] hover:stroke-white`}
        >
          <RightCaretIcon className="pl-1 w-[15px]" />
        </div>
          {content.length > 3 && (
            <div
              onClick={() => handleArrowClick('next')}
              onTouchEnd={() => handleArrowClick('next')}
              className={`flex mr-2 ml-4 md:ml-2 lg:hidden items-center justify-center stroke-[#7F898D] z-40`}
            >
              <RightCaretIcon className="pr-1 scale-[75%]" />
            </div>
          )}
      </>
    );
  };


  return (
    <div className="cursor-pointer no-select custom-carousel" cy-test-id="custom-carousel-with-thumbs">
      <Slider ref={slider} {...props.carouselOptions} afterChange={handleSlideChange}>
        {activeContent}
      </Slider>
      <div className='flex w-full h-full pt-2 md:items-center md:justify-center md:pt-4'>
        {renderLeftArrow()}
        {renderThumbnailContent()}
        {renderRightArrow()}
      </div>
    </div>
  );
};

export default CustomCarouselWithThumbs;
